/**
 * The two-act arrival (Task 12) — orchestrates hashSecret.ts, device.ts,
 * api.ts, retry.ts, and renderVennSvg into the stage a visitor actually
 * sees.
 *
 * Act 1, the token bloom: `mount()` plays it synchronously, before any
 * `await` — it must not wait on the network and must not imply anything
 * about the result (the plan's own words).
 *
 * Act 2, the assembly: only reachable through a successful enroll+resolve
 * (Task 4/7) AND a successful read (Task 8) — a doc that fails either
 * check never reaches `showAssembled`.
 *
 * Failure is never one state: a definitive denial settles CLOSED
 * (`copy.CLOSED_COPY`, tap-anywhere blooms a fresh empty stage); a
 * transient failure retries with the SAME in-memory secret (never
 * re-reading the URL) and shows a visibly different TRANSIENT state,
 * per Severino's review, finding 11c.
 *
 * SINGLE-BOOT ASSUMPTION, REMOVED (Severino's ride on PR #83, findings
 * 2/3/6). Three real timelines this class survives:
 *
 *  1. A second `start()` call: latched with a one-way `started` flag.
 *  2. A `pageshow` with `event.persisted` true (bfcache restore): main.ts
 *     wires this to `restart()`, below — never a second `start()`.
 *  3. A reload/Back after the strip: a null secret skips straight to
 *     `readEnvelope`, the same cookie-authenticated call a fragment-
 *     bearing visit makes after a successful resolve.
 *
 * BFCACHE, RECONSIDERED (Ferruccio's gate, PR #83, B4, blocking). The
 * first cut wiped `this.secret` unconditionally in `teardown()`
 * (`pagehide`) as hygiene — but a `pagehide` fires on EVERY navigation
 * away, including a tab merely frozen mid-resolve or mid-backoff, not
 * only one that's closing for good. Wiping the secret THEN meant a
 * `pageshow` restore had nothing left to retry with — Ferruccio
 * reproduced a permanently stuck bloom (frozen mid-flight) and a
 * permanently stuck "still finding its way to you…" (frozen during
 * retry backoff, which is ALWAYS bfcache-eligible: no request in
 * flight), each contradicting this very file's own claim that a failure
 * "must still resolve to a visible state — never a silently stuck
 * bloom." THE ARGUED FIX: `teardown()` no longer wipes the secret — it
 * only stops WASTED work (aborts the in-flight fetch, cancels the
 * pending timer) while the tab is hidden or frozen, which is the actual
 * "abandoned tab keeps POSTing" concern. The secret is wiped ONLY at the
 * one point it has genuinely finished its job: the instant resolve
 * succeeds (see `beginResolve` below) — never speculatively on
 * `pagehide`, which cannot tell "this tab is gone forever" apart from
 * "this tab is about to come right back." `restart()` (wired to
 * `pageshow`) then genuinely RESTARTS the machine from generation+1,
 * using whatever secret state honestly exists at that moment: still
 * held (resolve hadn't finished — retry it in full) or already null
 * (resolve had already succeeded, or there never was one — read via the
 * cookie, exactly like any other reload). The claim and the code now
 * agree. The tradeoff, named rather than hidden: a secret frozen
 * mid-resolve does sit in the bfcache snapshot a little longer than an
 * eager wipe would allow — accepted because Fork 6's own ruling already
 * treats revocation, not a clock or a wipe timed to a browser event, as
 * THE control here ("this is not a particularly secure system... it's a
 * toy"), and a browser's own bfcache snapshot is not a channel a remote
 * attacker reads.
 *
 * OVERLAPPING ATTEMPTS (Severino's ride, finding 4) get a monotonic
 * `generation` counter. Every scheduled retry/settle and every in-flight
 * fetch closes over the generation it was started in; a generation bump
 * (a manual retry click, a manual reset, `restart()`, or `teardown()`)
 * makes every check of `generation !== this.generation` in an older
 * attempt a silent no-op, and aborts its in-flight `AbortController`
 * outright.
 *
 * THE 401 AMBIGUITY (Ferruccio's gate, PR #83, B1, blocking — see
 * failure.ts's own module comment for the full argument): the merged
 * server's ONE denial shape (401, `{ok:false}`) is deliberately
 * identical whether the cause is a genuinely dead LINK or a bad/missing
 * DEVICE credential — that distinction is not recoverable from the wire
 * at all, on purpose ("no discovery of any kind"). `beginResolve` below
 * resolves the ambiguity the only honest way context allows: on a
 * RESOLVE-time denial, re-enroll a FRESH device credential (free,
 * always available) and retry ONCE before accepting "dead" — ruling out
 * "it's just my stale secret" first. A READ-time denial gets no such
 * second chance: Fork 6's own ruling means a session that once worked
 * and now doesn't has been REVOKED or epoch-killed, not merely aged out,
 * and no fresh device credential can rescue a revoked GRANT — there is
 * no analogous ambiguity to resolve there, so `classifyFailure`'s own
 * default answer stands.
 */

import { renderVennSvg } from '@zov/core';
import type { FragmentSource } from './hashSecret.ts';
import { captureAndStripSecret } from './hashSecret.ts';
import type { DeviceCredential, DeviceStorage } from './device.ts';
import { loadStoredDevice, storeDevice } from './device.ts';
import { enrollDevice, resolveLink, readEnvelope } from './api.ts';
import {
  classifyFailure,
  isGenericDenial,
  type FailureKind,
} from './failure.ts';
import { MAX_AUTO_RETRIES, backoffMs } from './retry.ts';
import {
  CLOSED_COPY,
  TRANSIENT_COPY,
  TRANSIENT_EXHAUSTED_COPY,
} from './copy.ts';
import { attachCamera, type Camera } from './camera.ts';

export type StageState =
  'bloom' | 'assembling' | 'settled' | 'closed' | 'transient';

/** How long the tap-anywhere "fresh bloom" acknowledgment plays before
 *  settling back to the closed stage (Ferruccio's gate, PR #83, I1: the
 *  old reset scheduled nothing, so the bloom ran forever with no way
 *  back — a permanent spinner over a dead link with nothing loading
 *  behind it). Matches `styles.css`'s own `token-bloom` animation
 *  duration (900ms) — the bloom plays ONE full cycle as an
 *  acknowledgment of the tap, not a promise that new data is coming,
 *  then rests back on the same "put away" stage. (A feel call, named
 *  here rather than silently baked in — Mathew's to overrule.) */
const BLOOM_ACK_MS = 900;

export interface ArrivalDeps {
  vennId: string;
  fragmentSource: FragmentSource;
  deviceStorage: DeviceStorage;
  fetchImpl: typeof fetch;
  /** Injectable for tests; production passes a `window.setTimeout` /
   *  `window.clearTimeout` pair. Returns a canceller so `teardown()` and
   *  a fresh generation can positively cancel a pending timer instead of
   *  merely hoping its callback notices it's stale. */
  scheduleRetry: (run: () => void, delayMs: number) => () => void;
}

export class ArrivalController {
  private readonly stage: HTMLElement;
  private readonly bloomToken: HTMLElement;
  private readonly vennMount: HTMLElement;
  private readonly statusLine: HTMLElement;
  private camera: Camera | null = null;
  private secret: string | null = null;
  private device: DeviceCredential | null = null;
  /** One-way latch (Severino's ride, finding 2): `start()` acts at most
   *  once per controller instance, ever. */
  private started = false;
  /** Bumped on every manual retry/reset, `restart()`, and `teardown()`.
   *  Any in-flight or scheduled work from an earlier generation checks
   *  this before touching the stage and no-ops if it's stale. */
  private generation = 0;
  private inFlight: AbortController | null = null;
  private cancelPending: (() => void) | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly deps: ArrivalDeps,
  ) {
    this.stage = must(root, '[data-role="stage"]');
    this.bloomToken = must(root, '[data-role="bloom-token"]');
    this.vennMount = must(root, '[data-role="venn-mount"]');
    this.statusLine = must(root, '[data-role="status-line"]');
    this.statusLine.addEventListener('click', () => {
      if (this.stage.dataset.state === 'closed') {
        this.resetToFreshBloom();
      }
      if (this.stage.dataset.state === 'transient') {
        // A manual retry races whatever this generation's own pending
        // timer was about to do — bump the generation first so that
        // timer's eventual firing is a no-op, then start clean.
        this.generation++;
        this.cancelPending?.();
        this.cancelPending = null;
        this.inFlight?.abort();
        this.inFlight = null;
        void this.beginResolve(0, this.generation, false);
      }
    });
  }

  /** Plays Act 1 immediately, then kicks off the network half. Call once,
   *  at first paint — a second call is a no-op (the `started` latch). */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.setState('bloom');
    this.secret = captureAndStripSecret(this.deps.fragmentSource);
    this.device = loadStoredDevice(this.deps.deviceStorage);
    void this.beginResolve(0, this.generation, false);
  }

  /** Wired to `pageshow` in main.ts: if the bfcache restored a page
   *  whose address bar shows the fragment again, strip it — and ONLY
   *  strip it. Never recaptures a secret; `restart()` is the separate,
   *  explicit re-entry point for actually resuming the arrival. */
  restripAddressBar(): void {
    this.deps.fragmentSource.stripFromAddressBar();
  }

  /** Wired to `pageshow` (persisted) in main.ts, alongside
   *  `restripAddressBar()` — Ferruccio's gate, PR #83, B4: genuinely
   *  RESTARTS the machine rather than leaving it frozen wherever
   *  `teardown()` left it. Never recaptures the fragment (that stays
   *  `restripAddressBar()`'s job, and `started`'s own latch would
   *  refuse a second `start()` regardless) — resumes with whatever
   *  `this.secret`/`this.device` honestly hold right now, which
   *  `beginResolve` already knows how to act on correctly either way. */
  restart(): void {
    this.generation++;
    this.cancelPending?.();
    this.cancelPending = null;
    this.inFlight?.abort();
    this.inFlight = null;
    this.vennMount.innerHTML = '';
    this.camera?.destroy();
    this.camera = null;
    this.setState('bloom');
    void this.beginResolve(0, this.generation, false);
  }

  /** Wired to `pagehide` in main.ts: stops WASTED work — aborts any
   *  in-flight fetch, cancels any pending retry/settle timer, bumps the
   *  generation — while this tab is hidden or frozen into the bfcache.
   *  Deliberately does NOT wipe `this.secret` (see this file's own
   *  module comment, "BFCACHE, RECONSIDERED", for why an eager wipe here
   *  broke `restart()`'s ability to resume honestly). Idempotent — safe
   *  to call more than once. */
  teardown(): void {
    this.generation++;
    this.inFlight?.abort();
    this.inFlight = null;
    this.cancelPending?.();
    this.cancelPending = null;
  }

  private resetToFreshBloom(): void {
    this.generation++;
    const generation = this.generation;
    this.cancelPending?.();
    this.cancelPending = null;
    this.inFlight?.abort();
    this.inFlight = null;
    this.vennMount.innerHTML = '';
    this.camera?.destroy();
    this.camera = null;
    this.setState('bloom');
    // Ferruccio's gate, PR #83, I1: this reset is decorative chrome over
    // a dead link — there is nothing left to fetch, so leaving 'bloom'
    // showing forever reads as a stuck spinner over nothing happening,
    // not a considered idle state. The bloom plays once, as an
    // acknowledgment of the tap, then rests back on the same closed
    // stage/copy. (A feel decision, not a claim of new data — Mathew's
    // to overrule.)
    this.cancelPending = this.deps.scheduleRetry(() => {
      if (generation !== this.generation) return;
      this.statusLine.textContent = CLOSED_COPY;
      this.setState('closed');
    }, BLOOM_ACK_MS);
  }

  private async beginResolve(
    attempt: number,
    generation: number,
    hasRetriedWithFreshDevice: boolean,
  ): Promise<void> {
    if (generation !== this.generation) return;
    this.inFlight?.abort();
    const controller = new AbortController();
    this.inFlight = controller;
    try {
      if (this.secret !== null) {
        if (this.device === null) {
          const enrolled = await enrollDevice(
            this.deps.fetchImpl,
            controller.signal,
          );
          if (generation !== this.generation) return;
          if (!enrolled.ok) {
            // enroll.ts has no notion of "denied" at all (it is
            // `capability: 'public'`) — its only failures are a rate
            // limit or its own 500, both already transient shapes, so
            // this can never surface as CLOSED.
            this.handleFailure(
              classifyFailure(enrolled.signal),
              attempt,
              generation,
            );
            return;
          }
          this.device = enrolled.device;
          storeDevice(this.deps.deviceStorage, this.device);
        }

        const resolveOutcome = await resolveLink(
          this.deps.vennId,
          this.secret,
          this.device.secret,
          this.deps.fetchImpl,
          controller.signal,
        );
        if (generation !== this.generation) return;
        if (!resolveOutcome.ok) {
          if (
            !hasRetriedWithFreshDevice &&
            isGenericDenial(resolveOutcome.signal)
          ) {
            // The ambiguous case this file's own module comment argues:
            // rule out "it's just my stale device secret" before ever
            // accepting "dead" for a resolve-time denial.
            const reenrolled = await enrollDevice(
              this.deps.fetchImpl,
              controller.signal,
            );
            if (generation !== this.generation) return;
            if (reenrolled.ok) {
              this.device = reenrolled.device;
              storeDevice(this.deps.deviceStorage, this.device);
              void this.beginResolve(attempt, generation, true);
              return;
            }
            // Couldn't even re-enroll — a real hiccup on its own terms,
            // never a verdict on the link.
            this.handleFailure(
              classifyFailure(reenrolled.signal),
              attempt,
              generation,
            );
            return;
          }
          this.handleFailure(
            classifyFailure(resolveOutcome.signal),
            attempt,
            generation,
          );
          return;
        }
        // The bearer LINK secret has done its one job (Task 7: single
        // exchange) — wipe it. Nothing downstream ever needs it again.
        this.secret = null;
      }

      const readOutcome = await readEnvelope(
        this.deps.vennId,
        this.deps.fetchImpl,
        controller.signal,
      );
      if (generation !== this.generation) return;
      if (!readOutcome.ok) {
        this.handleFailure(
          classifyFailure(readOutcome.signal),
          attempt,
          generation,
        );
        return;
      }
      this.showAssembled(readOutcome.envelope.doc, generation);
    } catch {
      // ANY throw here (a malformed envelope, an unexpected exception
      // inside renderVennSvg, anything at all) must still resolve to a
      // visible state — never a silently stuck bloom.
      if (generation !== this.generation) return;
      this.handleFailure('transient', attempt, generation);
    }
  }

  private handleFailure(
    kind: FailureKind,
    attempt: number,
    generation: number,
  ): void {
    if (generation !== this.generation) return;
    if (kind === 'dead') {
      this.statusLine.textContent = CLOSED_COPY;
      this.setState('closed');
      return;
    }
    if (attempt >= MAX_AUTO_RETRIES) {
      this.statusLine.textContent = TRANSIENT_EXHAUSTED_COPY;
      this.setState('transient');
      return;
    }
    this.statusLine.textContent = TRANSIENT_COPY;
    this.setState('transient');
    this.cancelPending?.();
    this.cancelPending = this.deps.scheduleRetry(() => {
      if (generation !== this.generation) return;
      void this.beginResolve(attempt + 1, generation, false);
    }, backoffMs(attempt));
  }

  private showAssembled(
    doc: import('@zov/core').VennDocumentV1,
    generation: number,
  ): void {
    if (generation !== this.generation) return;
    const idPrefix = `venn-${sanitizeIdPrefix(this.deps.vennId)}-`;
    const svgMarkup = renderVennSvg(doc, { idPrefix });
    this.vennMount.innerHTML = svgMarkup;
    const svgEl = this.vennMount.querySelector('svg');
    if (svgEl) {
      cascadeIn(svgEl, doc.sets.length);
      // Camera transforms the <svg> element itself — a CSS transform is
      // valid on any element, no wrapper required.
      this.camera = attachCamera(this.vennMount, svgEl);
    }
    this.setState('assembling');
    // Assembly is a one-shot cascade; once it has had time to play, the
    // stage is simply "settled" — a resting state, not a running one.
    const settleAfter = cascadeDurationMs(doc.sets.length);
    this.cancelPending?.();
    this.cancelPending = this.deps.scheduleRetry(() => {
      if (generation !== this.generation) return;
      this.setState('settled');
    }, settleAfter);
  }

  private setState(state: StageState): void {
    this.stage.dataset.state = state;
  }
}

function must(root: HTMLElement, selector: string): HTMLElement {
  const el = root.querySelector<HTMLElement>(selector);
  if (!el) throw new Error(`arrival.ts: stage markup is missing ${selector}`);
  return el;
}

/** `vennId` is server-controlled path content, not user-typed text, but
 *  this is still an id-namespace boundary (see render.ts's own comment) —
 *  cheap defense in depth costs nothing here. */
function sanitizeIdPrefix(vennId: string): string {
  return vennId.replace(/[^A-Za-z0-9_-]/g, '');
}

/** Adds the `cascade-piece` class to every top-level painted group
 *  (`renderVennSvg`'s regions, plus the label group) with a stagger
 *  derived from the document's own set count, per the plan's "cascade
 *  timing derived from the actual set count" line. This walks the
 *  ALREADY-PARSED DOM the browser produced from renderVennSvg's fixed,
 *  escaped output — it never re-templates or string-interpolates
 *  anything, so it cannot reopen the structural XSS wall render.ts
 *  documents; it only classes real Element nodes. */
function cascadeIn(svg: SVGElement, setCount: number): void {
  const groups = Array.from(svg.children).filter(
    (child): child is SVGGElement => child.tagName === 'g',
  );
  const stagger = 260 / Math.max(setCount, 1);
  groups.forEach((g, i) => {
    g.classList.add('cascade-piece');
    g.style.setProperty('--zov-cascade-delay', `${i * stagger}ms`);
  });
}

function cascadeDurationMs(setCount: number): number {
  const stagger = 260 / Math.max(setCount, 1);
  return setCount * stagger + 520; // + the piece's own animation length
}
